// Depends on adapter-7.4.0.min.js from https://github.com/webrtc/adapter
// Contains a updated version... DO NOT OVERRIDE!
import '../../../node_modules/webrtc-adapter/out/adapter';

class SrsError extends Error {
    constructor(name, message) {
        super(message);

        this.name = name;
    }
}

export class SrsRtcPublisherAsync {

    constructor(config) {
        this.pc = null;
        this.stream = new MediaStream();
        this.localStream = null;

        this.config = config;
        this.tid = Number(parseInt(new Date().getTime() * Math.random() * 100)).toString(16).slice(0, 7);
    }

    async initLocalStream() {
        this.localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: this.config.audioDeviceId,
            },
            video: {
                deviceId: this.config.videoDeviceId,
                ...this.getVideoConfig(),
            },
        });
    }

    async setVideoSource(deviceId) {
        if (!deviceId) {
            return;
        }

        this.config.videoDeviceId = deviceId;

        this.localStream = await navigator.mediaDevices.getUserMedia({
            video: {
                deviceId,
                ...this.getVideoConfig(),
            },

            audio: this.config.audioDeviceId ? {
                deviceId: this.config.audioDeviceId,
            } : true,
        });

        this.updateTracks();
    }

    getVideoConfig() {
        if (!this.config.forceHighResolution) {
            return {};
        }

        return {
            width: {
                ideal: 4096,
            },
            height: {
                ideal: 2160,
            },
        };
    }

    async setAudioSource(deviceId) {
        if (!deviceId) {
            return;
        }

        this.config.audioDeviceId = deviceId;

        this.localStream = await navigator.mediaDevices.getUserMedia({
            video: this.config.videoDeviceId ? {
                deviceId: this.config.videoDeviceId,
                ...this.getVideoConfig(),
            } : true,

            audio: {
                deviceId,
            },
        });

        this.updateTracks();
    }

    updateTracks() {
        if (!this.pc) {
            return;
        }

        this.pc.getSenders().forEach(sender => {
            this.localStream.getTracks().forEach((track) => {
                if (track.kind === sender.track.kind) {
                    sender.replaceTrack(track);
                }
            });
        });
    }

    async publish(url, forceHighBandwidth) {
        await this.close();

        this.pc = new RTCPeerConnection(null);

        this.pc.addTransceiver('audio', {
            direction: 'sendonly',
        });

        const videoTransceiver = this.pc.addTransceiver('video', {
            direction: 'sendonly',
            // Force high bandwidth and resolution
            sendEncodings: [
                {
                    active: true,
                    priority: 'high',
                    networkPriority: 'high',
                    scaleResolutionDownBy: 1,
                },
            ],
        });

        // Does not work on every device...
        if (forceHighBandwidth) {
            // Force high bandwidth and resolution
            videoTransceiver.setCodecPreferences([
                {
                    clockRate: 90000,
                    mimeType: 'video/H264',
                    sdpFmtpLine: 'level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f',
                },
                {
                    clockRate: 90000,
                    mimeType: 'video/red',
                },
            ]);
        }

        if (this.config.onConnectionStateChange) {
            this.pc.addEventListener('connectionstatechange', this.config.onConnectionStateChange);
        }
        //this.pc.addTransceiver("video", {direction: "sendonly"});
        //this.pc.addTransceiver("audio", {direction: "sendonly"});

        if (!navigator.mediaDevices && window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
            throw new SrsError('HttpsRequiredError', `Please use HTTPS or localhost to publish, read https://github.com/ossrs/srs/issues/2762#issuecomment-983147576`);
        }

        if (!this.localStream) {
            await this.initLocalStream();
        }

        // @see https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/addStream#Migrating_to_addTrack
        this.localStream.getTracks().forEach((track) => {
            if (track.kind === 'audio') {
                track = this.applyAudioEffectsToTrack(this.localStream);
            }

            this.pc.addTrack(track);

            // Notify about local track when stream is ok.
            this.onTrack({
                track: track,
            });
        });

        const offer = await this.pc.createOffer();

        await this.pc.setLocalDescription(offer);

        const session = await new Promise((resolve, reject) => {
            // @see https://github.com/rtcdn/rtcdn-draft
            const data = {
                tid: this.tid,
                streamurl: url,
                clientip: null,
                sdp: offer.sdp,
            };

            const xhr = new XMLHttpRequest();
            xhr.onload = function() {
                if (xhr.readyState !== xhr.DONE) return;
                if (xhr.status !== 200 && xhr.status !== 201) return reject(xhr);
                const data = JSON.parse(xhr.responseText);
                // console.log('Offer response', JSON.stringify(data));
                return data.code ? reject(xhr) : resolve(data);
            };

            xhr.open('POST', this.config.apiUrl + '/api/call/stream/publish', true);
            xhr.setRequestHeader('Content-type', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer ' + this.config.apiToken);
            xhr.send(JSON.stringify({
                stream: data,
            }));
        });

        await this.pc.setRemoteDescription(
            new RTCSessionDescription({
                type: 'answer',
                sdp: session.sdp,
            }),
        );

        // session.simulator = this.config.schema + '//' + this.config.urlObject.server + ':' + this.config.port + '/rtc/v1/nack/';

        return session;
    }

    applyAudioEffectsToTrack(mediaSource) {
        // https://www.tothenew.com/blog/enhancing-webrtc-audio-quality-with-web-audio-api/
        const audioCtx = new AudioContext();
        const audioSource = audioCtx.createMediaStreamSource(mediaSource);

        const gainNode = audioCtx.createGain();
        gainNode.gain.value = 1.5;

        const highPassFilter = audioCtx.createBiquadFilter();
        const lowPassFilter = audioCtx.createBiquadFilter();

        highPassFilter.type = 'highpass';
        highPassFilter.frequency.value = 100;

        lowPassFilter.type = 'lowpass';
        lowPassFilter.frequency.value = 9000;

        const settings = {
            threshold: -20, // This is the volume level where compression starts. Sounds louder than -20 dB will be turned down.
            knee: 40, // This softens how quickly compression kicks in, making the volume reduction smoother and less abrupt.
            ratio: 4, // For every 4 dB above the threshold, only 1 dB gets through. This controls how much loud sounds are reduced.
            attack: 0.3,// This is how quickly the compressor starts working after the sound gets too loud. A short delay keeps the initial impact of the sound.
            release: 0.25,// This is how long the compressor takes to stop after the sound drops below the threshold, ensuring smooth transitions back to normal volume.
        };

        const compressor = audioCtx.createDynamicsCompressor();
        for (const key in settings) {
            compressor[key].setValueAtTime(settings[key], audioCtx.currentTime);
        }

        audioSource.connect(gainNode);
        gainNode.connect(highPassFilter);
        highPassFilter.connect(lowPassFilter);
        lowPassFilter.connect(compressor);

        const modifiedStream = audioCtx.createMediaStreamDestination();
        compressor.connect(modifiedStream);

        return modifiedStream.stream.getAudioTracks()[0];
    };

    // Close the publisher.
    async close() {
        this.pc && this.pc.close();
        this.pc = null;
    }

    onTrack(event) {
        this.stream.addTrack(event.track);
    }
}

export class SrsRtcPlayerAsync {

    constructor(config) {
        this.pc = null;
        this.videoStream = new MediaStream();
        this.audioStream = new MediaStream();

        this.config = config;
        this.tid = Number(parseInt(new Date().getTime() * Math.random() * 100)).toString(16).slice(0, 7);
    }

    async play(videoElement, audioElement, url) {
        await this.close();

        this.pc = new RTCPeerConnection(null);

        this.pc.ontrack = (event) => {
            this.onTrack(event);
        };

        this.pc.addTransceiver('audio', {
            direction: 'recvonly',
        });
        this.pc.addTransceiver('video', {
            direction: 'recvonly',
        });

        if (this.config.onConnectionStateChange) {
            this.pc.addEventListener('connectionstatechange', this.config.onConnectionStateChange);
        }

        //self.pc.addTransceiver("video", {direction: "recvonly"});
        //self.pc.addTransceiver("audio", {direction: "recvonly"});

        const offer = await this.pc.createOffer();

        await this.pc.setLocalDescription(offer);

        const session = await new Promise((resolve, reject) => {
            // @see https://github.com/rtcdn/rtcdn-draft
            const data = {
                api: this.config.apiUrl,
                tid: this.tid,
                streamurl: url,
                clientip: null,
                sdp: offer.sdp,
            };

            // console.log('Offer data', JSON.stringify(data));

            const xhr = new XMLHttpRequest();

            xhr.onload = function() {
                if (xhr.readyState !== xhr.DONE) {
                    return;
                }
                if (xhr.status !== 200 && xhr.status !== 201) {
                    return reject(xhr);
                }

                const data = JSON.parse(xhr.responseText);
                // console.log('Offer response', JSON.stringify(data));
                return data.code ? reject(xhr) : resolve(data);
            };

            xhr.open('POST', this.config.apiUrl + '/api/call/stream/play', true);
            xhr.setRequestHeader('Content-type', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer ' + this.config.apiToken);
            xhr.send(JSON.stringify({
                stream: data,
            }));
        });

        await this.pc.setRemoteDescription(
            new RTCSessionDescription({
                type: 'answer',
                sdp: session.sdp,
            }),
        );

        videoElement.srcObject = this.videoStream;
        audioElement.srcObject = this.audioStream;

        // session.simulator = conf.schema + '//' + conf.urlObject.server + ':' + conf.port + '/rtc/v1/nack/';

        return session;
    }

    close() {
        this.pc && this.pc.close();
        this.pc = null;
    }

    onTrack(event) {
        if (event.track.kind === 'video') {
            this.videoStream.addTrack(event.track);
        }
        if (event.track.kind === 'audio') {
            this.audioStream.addTrack(event.track);
        }
    }
}
